import { type ForwardedRef, forwardRef, useEffect } from 'react';

import { type ListJobsQuery, type ListJobsQueryVariables, useListJobsQuery } from '@/common/graphql';

import type { ComboboxItemWithFormatted, CommonProps, UseQueryReturn } from '../props';
import { SelectMultiple, type SelectMultipleProps } from '../SelectMultiple';
import { SelectSingle, type SelectSingleProps } from '../SelectSingle';

export type JobSelectVariables = Partial<Omit<ListJobsQueryVariables, 'pageNumber' | 'pageSize' | 'keyword'>>;
type Data = ComboboxItemWithFormatted & Omit<ListJobsQuery['listJobs']['items'][number], 'id' | 'name' | 'enabled'>;
type Props = CommonProps<JobSelectVariables>;

const useQuery = (
  variables: JobSelectVariables | undefined,
  onLoadingCompleted?: () => void,
): UseQueryReturn<ListJobsQueryVariables, Data> => {
  const { data, ...rest } = useListJobsQuery({
    variables: {
      pageNumber: null,
      pageSize: null,
      keyword: null,
      enabled: variables?.enabled ?? null,
      hasEmployees: variables?.hasEmployees ?? null,
      onlyIds: variables?.onlyIds ?? null,
    },
    fetchPolicy: 'cache-first',
  });

  useEffect(() => {
    if (onLoadingCompleted) onLoadingCompleted();
  }, [onLoadingCompleted]);

  return {
    data:
      data?.listJobs?.items?.map(it => ({
        ...it,
        value: it.id,
        label: it.name,
        disabled: !it.enabled,
      })) || [],
    ...rest,
  };
};

export const JobSelectSingle = forwardRef(
  (props: Props & SelectSingleProps<Data>, ref: ForwardedRef<HTMLDivElement>) => {
    const { data, loading } = useQuery(props.variables, props.onLoadingCompleted);
    return <SelectSingle ref={ref} {...props} data={data} loading={loading} />;
  },
);

export const JobSelectMultiple = (props: Props & SelectMultipleProps<Data>) => {
  const { data, loading } = useQuery(props.variables, props.onLoadingCompleted);
  return <SelectMultiple multipleSelectedLabel={length => `${length} jobs`} {...props} data={data} loading={loading} />;
};
